import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import { formatLastUpdated } from '../../../services/functions'
import { statusLabel, statusTone } from '../../IngestRequests/requestContract'
import checkmark_full from '../../../Components/icons/checkmark_full.png'
import checkmark_empty from '../../../Components/icons/checkmark_empty.png'
import classes from '../featuredCollectionUpdate.module.css'
import detail from '../../IngestStatus/detail.module.css'
import ingestClasses from '../../IngestStatus/index.module.css'

const LIST_TOKEN = 'create.trending'

// New York is where the chart sweep runs (04:00 America/New_York) - shown
// alongside the relative time so an operator can line it up with that.
function formatNewYork(dateString) {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    dateStyle: 'medium',
    timeStyle: 'short',
  }) + ' NY'
}

// Only formatLastUpdated's short relative phrasing (under 3 days) is used
// here, to avoid a 2nd clock next to formatNewYork's absolute NY time.
function recentRelative(dateString) {
  if (!dateString) return null
  const diffDays = Math.floor((Date.now() - new Date(dateString)) / 86400000)
  return diffDays < 3 ? formatLastUpdated(dateString) : null
}

// Read-only Create tab trending list (app token create.trending). Lives as
// a tab on /featured-collection-update, next to Discovery - no edit/save
// workflow of its own, so it keeps its own isolated query/state.
export default function CreateTrendingTab() {
  const { getCreateTrendingSongs } = useAPI()

  // One call: songs in Redis order plus the newest chart sweep.
  const trendingQuery = useQuery({
    queryKey: ['CreateTrendingSongs'],
    queryFn: () => getCreateTrendingSongs(LIST_TOKEN),
    // A 404 here is final (missing app, or the route not deployed yet) -
    // don't retry, so the error state shows right away.
    retry: false,
  })

  const list = trendingQuery.data?.list
  const songs = trendingQuery.data?.songs || []
  const latestSweep = trendingQuery.data?.latest_chart_sweep || null

  return (
    <div className={classes.tabBody}>
      <p className={`${detail.sectionNote} ${classes.intro}`}>
        Read-only. The YouTube Music trending list shown on the app&apos;s Create tab (app token <code>{LIST_TOKEN}</code>), in its current order.
      </p>

      {trendingQuery.isLoading && <p role="status">Loading the trending list…</p>}
      {trendingQuery.isError && (
        <p className={classes.error} role="alert">
          {/* Only the route's own missing-app code means that - any other
              404 (e.g. the route itself not deployed yet) gets the generic
              message instead, with the status code if there is one. */}
          {trendingQuery.error?.code === 'COLLECTIONAPPNOTFOUND'
            ? `No collection app with token "${LIST_TOKEN}" was found in this environment.`
            : `The trending list could not be loaded.${trendingQuery.error?.status ? ` (${trendingQuery.error.status})` : ''}`}
        </p>
      )}

      {list && (
        <dl className={detail.facts}>
          <div>
            <dt>Last changed</dt>
            <dd>
              {recentRelative(list.date_updated) && `${recentRelative(list.date_updated)} `}
              {formatNewYork(list.date_updated)}
            </dd>
          </div>
          <div>
            <dt>Latest chart sweep</dt>
            <dd>
              {/* A load failure is already shown by the error banner above. */}
              {!latestSweep && 'No chart sweep found yet.'}
              {latestSweep && (
                <>
                  <Link to={'/ingest/' + latestSweep.id} className={classes.link}>
                    {latestSweep.source_id}
                  </Link>
                  {' '}
                  <span className={`${ingestClasses.badge} ${ingestClasses['tone_' + statusTone(latestSweep)] || ''}`}>
                    {statusLabel(latestSweep)}
                  </span>
                  {' · '}{formatNewYork(latestSweep.date_created)}
                </>
              )}
            </dd>
          </div>
        </dl>
      )}

      {trendingQuery.isSuccess && (
        <section className={classes.card}>
          {songs.length === 0 && <p>The list is empty.</p>}
          {songs.length > 0 && (
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>#</th>
                  <th className={classes.th}>Song</th>
                  <th className={classes.th}>Artist</th>
                  <th className={classes.th}>Sync verified</th>
                  <th className={classes.th}>song_f_id</th>
                </tr>
              </thead>
              <tbody>
                {songs.map((song) => (
                  <tr key={song.position} className={classes.row}>
                    <td className={classes.td}>{song.position}</td>
                    <td className={classes.td}>
                      {song.missing
                        ? <span className={classes.muted}>missing song</span>
                        : <Link to={'/song/' + song.f_id} className={classes.link}>{song.name}</Link>}
                    </td>
                    <td className={classes.td}>{song.missing ? '—' : (song.artist_name || '—')}</td>
                    <td className={classes.td}>
                      <img
                        width="18"
                        height="18"
                        className={classes.verifiedIcon}
                        src={song.is_sync_verified ? checkmark_full : checkmark_empty}
                      />
                    </td>
                    <td className={classes.td}>
                      <span className={classes.songFid}>{song.f_id}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  )
}
